import { mergeDeepAll } from '../utils'
import {
  CompassAPIDispersionChartData,
  CompassAPIMultiSeriesChartData,
  convertCompassToHighchartsDataFormat,
  dataLabelsFormatter,
  getAreaRangeData,
  getCompassAPIChartData,
  getMultipleLineChartProps,
  getMultipleLineStrCategoriesChartProps,
  getDataSources,
  magnitudePointFormatter,
  tooltipFormatterScatter,
} from '../utils/compass'
import { range6m1yAll } from '../utils/config'
import { Palette } from '../utils/constants'
import { capitalizeString } from '../utils/string'
import type { BaseContext } from './'
import { CommonChartConfig, commonChartOptions } from './common'

export namespace Oil {
  export namespace BrentWti {
    const CONTEXT_ID = 'equity:oil:brent_wti'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'brent_wti_first'
        | 'brent_sixth_first'
        | 'brent_twelfth_first'
        | 'wti_sixth_first'
        | 'wti_twelfth_first'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getMultiLineChartProps(config, data),
    }

    const getMultiLineChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        msdIndicators: { avg: true, sd1: false, sd2: false },
        yAxis: [{ id: 'absolute', title: { text: '$/bbl' } }],
      },
      {
        showInLegend: [false, false],
        yAxis: ['absolute', 'absolute'],
        visible: [true, false],
      }
    )
  }

  export namespace Refine {
    const CONTEXT_ID = 'equity:oil:refine'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'gulf_3m' | 'asia_3m' | 'europe_3m' | 'gulf_asia_europe_3m'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getAreaRangeProps(config, data),
    }

    const getAreaRangeProps = (config: ChartConfig, data: ChartData) => {
      const rangeValues: Record<number, { min?: number; max?: number }> = {}
      convertCompassToHighchartsDataFormat(data.data[2].data).forEach(
        ([x, value]) => {
          rangeValues[x] = { min: value }
        }
      )
      convertCompassToHighchartsDataFormat(data.data[0].data).forEach(
        ([x, value]) => {
          rangeValues[x] = { ...(rangeValues[x] ?? {}), max: value }
        }
      )

      const series = [
        {
          id: `${config.chart}-0`,
          type: 'spline',
          name: data.data[3].name,
          tooltip: { valueDecimals: 2 },
          yAxis: 'default',
          zIndex: 1,
          data: convertCompassToHighchartsDataFormat(data.data[3].data),
        },
        {
          id: `${config.chart}-1`,
          type: 'spline',
          name: data.data[4].name,
          tooltip: { valueDecimals: 2 },
          yAxis: 'default',
          zIndex: 1,
          data: convertCompassToHighchartsDataFormat(data.data[4].data),
        },
        {
          id: `${config.chart}-avg`,
          type: 'spline',
          dashStyle: 'LongDash',
          name: data.data[1].name,
          tooltip: { valueDecimals: 2 },
          yAxis: 'default',
          zIndex: 1,
          data: convertCompassToHighchartsDataFormat(data.data[1].data),
        },
        {
          id: `${config.chart}-range`,
          type: 'arearange',
          color: Palette.gray,
          enableMouseTracking: false,
          showInLegend: false,
          name: 'Average 2016 - 2019',
          grouping: false,
          yAxis: 'default',
          xAxis: 0,
          zIndex: 0,
          data: getAreaRangeData(rangeValues),
        },
      ]

      const yAxis = [{ id: 'default', opposite: false }]

      return {
        id: Context.getChartId(config),
        title: data.title,
        options: {
          ...commonChartOptions,
          ...getDataSources(data.data_sources),
          rangeSelector: range6m1yAll,
          series,
          tooltip: { xDateFormat: '%A, %b %e, %Y' },
          yAxis,
        },
        stockTools: true,
      }
    }
  }

  export namespace CrudeDemand {
    const CONTEXT_ID = 'equity:oil:crude_demand'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'china_yoy' | 'india_yoy'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(
          Context,
          {
            addMinMaxIndicators: true,
            msdIndicators: { avg: true, sd1: false, sd2: false },
            yAxis: [{ id: 'absolute', title: { text: 'mmbpd' } }],
          },
          {
            showInLegend: [false, false],
            yAxis: ['absolute', 'absolute'],
            visible: [true, false],
          }
        )(config, data),
    }
  }

  export namespace CrudeProduction {
    const CONTEXT_ID = 'equity:oil:crude_production'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'opec'
        | 'opec_yoy'
        | 'saudi_arabia'
        | 'saudi_arabia_yoy'
        | 'russia'
        | 'russia_yoy'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(
          Context,
          {
            addMinMaxIndicators: true,
            msdIndicators: { avg: true, sd1: false, sd2: false },
            yAxis: [{ id: 'absolute', title: { text: 'mmbpd' } }],
          },
          {
            showInLegend: [false, false],
            yAxis: ['absolute', 'absolute'],
            visible: [true, false],
          }
        )(config, data),
    }
  }

  export namespace CrudeNglUs {
    const CONTEXT_ID = 'equity:oil:crude_ngl_us'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'crude_ngl' | 'crude_ngl_yoy' | 'us_crude' | 'us_crude_yoy'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const isYoyChart = (chart: string) =>
      chart === 'crude_ngl_yoy' || chart === 'us_crude_yoy'

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(
          Context,
          {
            ...(isYoyChart(config.chart)
              ? {
                  convertToPercentages: data.data.map((s) =>
                    s.name.includes('%')
                  ),
                  yAxis: [
                    { id: 'primary', height: '60%' },
                    {
                      height: '40%',
                      id: 'secondary',
                      labelSuffix: '%',
                      opposite: true,
                      top: '60%',
                    },
                  ],
                }
              : {}),
            tooltip: {
              ...(config.chart !== 'crude_ngl'
                ? { shared: true, split: false, xDateFormat: '%b/%Y' }
                : { xDateFormat: '%A, %b %e, %Y' }),
            },
          },
          {
            ...(isYoyChart(config.chart)
              ? {
                  color:
                    config.chart === 'crude_ngl_yoy'
                      ? [
                          Palette.darkBlue,
                          Palette.navyBlue,
                          Palette.darkGreen,
                          Palette.green,
                        ]
                      : config.chart === 'us_crude_yoy'
                      ? [
                          Palette.darkBlue,
                          Palette.darkGreen,
                          Palette.darkGray,
                          Palette.darkYellow,
                          Palette.navyBlue,
                          Palette.green,
                          Palette.gray,
                          Palette.yellow,
                        ]
                      : undefined,
                  type: data.data.map((s) =>
                    s.name.includes('%') ? 'column' : 'spline'
                  ),
                  yAxis: data.data.map((s) =>
                    s.name.includes('%') ? 'secondary' : 'primary'
                  ),
                }
              : {}),
          }
        )(config, data),
    }
  }

  export namespace WtiPosition {
    const CONTEXT_ID = 'equity:oil:wti_position'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'long'
        | 'short'
        | 'net'
        | 'net_long_open_interest'
        | 'producer_short'
        | 'other_net_long_oi_vs_wti'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'long':
          case 'short':
          case 'net':
          case 'producer_short':
            return getMultiLineChartProps(config, data)
          case 'net_long_open_interest':
            return getNetLongOpenInterestChartProps(config, data)
          case 'other_net_long_oi_vs_wti':
            return getOtherNetLongOiVsWtiChartProps(config, data)
        }
      },
    }

    const getMultiLineChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        msdIndicators: { avg: true, sd1: false, sd2: false },
        yAxis: [{ id: 'absolute', title: { text: 'Contratos' } }],
      },
      {
        showInLegend: [false, false],
        yAxis: ['absolute', 'absolute'],
        visible: [true, false],
      }
    )

    const getNetLongOpenInterestChartProps = getMultipleLineChartProps(
      Context,
      {
        convertToPercentages: [true],
      },
      {
        showInLegend: [true, false],
        visible: [true, false],
      }
    )

    const getOtherNetLongOiVsWtiChartProps = getMultipleLineChartProps(
      Context,
      {
        convertToPercentages: [true, false],
        yAxis: [
          { id: 'absolute', title: { text: 'WTI ($/bbl)' }, opposite: true },
          { id: 'relative', title: { text: '% of OI' } },
        ],
      },
      {
        yAxis: ['absolute', 'relative'],
      }
    )
  }

  export namespace BrentPosition {
    const CONTEXT_ID = 'equity:oil:brent_position'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'long'
        | 'short'
        | 'net'
        | 'net_long_open_interest'
        | 'producer_short'
        | 'other_net_long_oi_vs_brent'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'long':
          case 'short':
          case 'net':
          case 'producer_short':
            return getMultiLineChartProps(config, data)
          case 'net_long_open_interest':
            return getNetLongOpenInterestChartProps(config, data)
          case 'other_net_long_oi_vs_brent':
            return getOtherNetLongOiVsBrentChartProps(config, data)
        }
      },
    }

    const getMultiLineChartProps = getMultipleLineChartProps(
      Context,
      {
        addMinMaxIndicators: true,
        msdIndicators: { avg: true, sd1: false, sd2: false },
        tooltip: { pointFormatter: magnitudePointFormatter() },
        yAxis: [{ id: 'absolute', title: { text: 'Contratos' } }],
      },
      {
        showInLegend: [false, false],
        yAxis: ['absolute', 'absolute'],
        visible: [true, false],
      }
    )

    const getNetLongOpenInterestChartProps = getMultipleLineChartProps(
      Context,
      {
        convertToPercentages: [true],
      },
      {
        showInLegend: [true, false],
        visible: [true, false],
      }
    )

    const getOtherNetLongOiVsBrentChartProps = getMultipleLineChartProps(
      Context,
      {
        convertToPercentages: [true, false],
        yAxis: [
          { id: 'absolute', title: { text: 'WTI ($/bbl)' }, opposite: true },
          { id: 'relative', title: { text: '% of OI' } },
        ],
      },
      {
        yAxis: ['absolute', 'relative'],
      }
    )
  }

  export namespace BrentWtiPosition {
    const CONTEXT_ID = 'equity:oil:brent_wti_position'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'long'
        | 'short'
        | 'net'
        | 'net_long_open_interest'
        | 'producer_short'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(
          Context,
          {
            addMinMaxIndicators: true,
            ...(config.chart === 'net_long_open_interest'
              ? { convertToPercentages: [true] }
              : {
                  msdIndicators: { avg: true, sd1: false, sd2: false },
                  yAxis: [{ id: 'absolute', title: { text: 'Contratos' } }],
                }),
            tooltip: {
              pointFormatter: magnitudePointFormatter({
                unit: config.chart === 'net_long_open_interest' ? '%' : '',
              }),
            },
          },
          {
            ...(config.chart !== 'net_long_open_interest'
              ? { yAxis: ['absolute', 'absolute'] }
              : {}),
            showInLegend: [false, false],
            visible: [true, false],
          }
        )(config, data),
    }
  }

  export namespace Dispersion {
    const CONTEXT_ID = 'equity:oil:dispersion'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'price_inventories'
        | 'price_inventories_demand'
        | 'price_inventories_delta'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIDispersionChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => getDispersionChartProps(config, data),
    }

    const getDispersionChartProps = (
      config: ChartConfig,
      data: CompassAPIDispersionChartData
    ) => {
      const series = data.data.map((s, idx) => ({
        animation: false,
        id: `series-${idx}`,
        name: s.name,
        data: s.values.map((p) => ({
          name: p.label ?? undefined,
          x: p.x,
          y: p.y,
        })),
        marker: {
          radius: idx === 0 ? 1.25 : 4,
        },
        dataLabels: {
          allowOverlap: false,
          backgroundColor: '#F4F6F8',
          borderColor: '#AAAAAA',
          borderRadius: 5,
          borderWidth: 0.5,
          enabled: true,
          formatter: dataLabelsFormatter,
          useHtml: true,
          zIndex: 10,
        },
        turboThreshold: 0,
        showInLegend: false,
      }))

      const tooltip = {
        backgroundColor: 'rgba(247,247,247,0.85)',
        enabled: true,
        formatter: tooltipFormatterScatter({
          xAxisLabel: 'mmbbls',
          yAxisLabel: '$/bbl',
        }),
        shared: false,
        style: {
          color: '#333333',
          cursor: 'default',
          fontSize: '12px',
          whiteSpace: 'nowrap',
        },
        useHTML: true,
        zIndex: 1000,
      }

      return {
        id: Context.getChartId(config),
        options: mergeDeepAll([
          commonChartOptions,
          getDataSources(data.data_sources),
          {
            title: { align: 'center', text: data.title },
            chart: {
              type: 'scatter',
              zoomType: 'xy',
              animation: false,
            },
            plotOptions: {
              series: {
                animation: false,
                cluster: { enabled: true },
                crisp: false,
                dataGrouping: { enabled: false },
                shadow: false,
              },
            },
            series,
            tooltip,
            stockTools: { gui: { enabled: false } },
            xAxis: { title: { text: 'mmbbls' } },
            yAxis: { title: { text: '$/bbl' } },
          },
        ]),
        stockTools: false,
        useRegularChart: true,
      }
    }
  }

  export namespace SupplyDemand {
    const CONTEXT_ID = 'equity:oil:supply_demand'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'non_opec_supply_last'
        | 'non_opec_supply'
        | 'world_demand_last'
        | 'world_demand'
        | 'non_opec_supply_last_yoy'
        | 'non_opec_supply_yoy'
        | 'world_demand_last_yoy'
        | 'world_demand_yoy'
        | 'supply_demand'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'non_opec_supply_last':
          case 'world_demand_last':
          case 'non_opec_supply_last_yoy':
          case 'world_demand_last_yoy':
            return getMultipleLineChartPropsWrapper(
              config,
              data,
              {
                yAxis: [{ title: { text: 'mb/d' } }],
              },
              {
                type: Array(3).fill('spline'),
              }
            )
          case 'non_opec_supply':
          case 'world_demand':
          case 'non_opec_supply_yoy':
          case 'world_demand_yoy':
            return getMultipleLineChartPropsWrapper(
              config,
              data,
              {
                yAxis: [{ title: { text: 'mb/d' } }],
              },
              {
                color: [Palette.darkBlue, Palette.darkGray],
                type: Array(3).fill('spline'),
              }
            )
          case 'supply_demand':
            return getMultipleLinesStrCategoriesProps(config, data)
        }
      },
    }

    const getMultipleLineChartPropsWrapper = (
      config: ChartConfig,
      data: ChartData,
      chartOptions: any,
      options?: any
    ) => getMultipleLineChartProps(Context, chartOptions, options)(config, data)

    const getMultipleLinesStrCategoriesProps = (
      config: ChartConfig,
      data: ChartData
    ) => {
      const convertDateToQuarter = (date: string) =>
        `Q${(parseInt(date.split('-')[1]) + 2) / 3}'${date.substr(2, 2)}`

      const fillWithNullPoints = (data: { date: string; value: number }[]) =>
        data.map((el) => ({
          date: el.date,
          value: null,
        }))

      const convertData = (response: ChartData) => ({
        ...response,
        data: [
          {
            ...response.data[1],
            name: capitalizeString(response.data[0].name),
            data: [
              ...response.data[1].data,
              ...fillWithNullPoints(response.data[0].data),
            ],
          },
          {
            ...response.data[0],
            name: capitalizeString(response.data[1].name),
            data: [
              ...fillWithNullPoints(response.data[1].data),
              ...response.data[0].data,
            ],
          },
        ],
      })
      return getMultipleLineStrCategoriesChartProps(
        Context,
        {
          tooltip: {
            formatter: function () {
              const _this = this as {
                points: {
                  series: { name: string }
                }[]
                x: string
                y: number
              }
              return `${_this.points[0].series.name}<br/><b>${
                _this.x
              }:</b> ${_this.y.toFixed(2)}`
            },
          },
          yAxis: [{ title: { text: 'mmbpd' } }],
          xAxisCategories: [
            ...data.data[1].data.map((item) => item.date.split('-')[0]),
            ...data.data[0].data.map((item) => convertDateToQuarter(item.date)),
          ],
        },
        {
          type: Array(data.data.length).fill('spline'),
        }
      )(config, convertData(data) as CompassAPIMultiSeriesChartData)
    }
  }

  export namespace UsOilRig {
    const CONTEXT_ID = 'equity:oil:us_oil_rig'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart: 'rig_count_us'
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) =>
        getMultipleLineChartProps(Context, {
          addMinMaxIndicators: true,
          tooltip: { pointFormatter: magnitudePointFormatter() },
        })(config, data),
    }
  }

  export namespace Valuation {
    const CONTEXT_ID = 'equity:oil:valuation'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      chart:
        | 'ev_ebitda_petr4'
        | 'ev_ebitda_ratio_petr4_tier1'
        | 'ev_ebitda_ratio_petr4_tier2'
        | 'ev_ebitda_ratio_tier1_tier2'
        | 'ev_ebitda_tier1_historical_series'
        | 'ev_ebitda_tier2_historical_series'

      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.chart}`,
      getChartData: getCompassAPIChartData,
      getChartProps: (config, data) => {
        switch (config.chart) {
          case 'ev_ebitda_petr4':
            return getValuationChartProps(
              config,
              data,
              'EV/EBITDA F12m Petrobrás'
            )
          case 'ev_ebitda_ratio_petr4_tier1':
            return getRatioChartProps(
              config,
              data,
              'Ratio EV/EBITDA F12m Petrobrás / Tier1 (Shell, Exxon, Chevron, Total, BP)',
              ['Ratio', 'Petrobrás', 'Tier 1']
            )
          case 'ev_ebitda_ratio_petr4_tier2':
            return getRatioChartProps(
              config,
              data,
              'Ratio EV/EBITDA F12m Petrobrás / Tier2 (Rosneft, Lukoil, Eni, CNOOC)',
              ['Ratio', 'Petrobrás', 'Tier 2']
            )
          case 'ev_ebitda_ratio_tier1_tier2':
            return getRatioChartProps(
              config,
              data,
              'Ratio EV/EBITDA F12m Tier 1 (RDSA, XOM, CVX, FP e BP) / Tier2 (ROSN, LKOH, ENI e 883)',
              ['Ratio', 'Tier 1', 'Tier 2']
            )
          case 'ev_ebitda_tier1_historical_series':
            return getValuationChartProps(
              config,
              data,
              'EV/EBITDA F12m Tier1 (Shell, Exxon, Chevron, Total, BP)'
            )
          case 'ev_ebitda_tier2_historical_series':
            return getValuationChartProps(
              config,
              data,
              'EV/EBITDA F12m Tier2 (Rosneft, Lukoil, ENI, CNOOC)'
            )
        }
      },
    }

    const getValuationChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData,
      title: string
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          chartTitle: title,
          msdIndicators: { avg: true, sd1: true, sd2: true },
        },
        { name: [''] }
      )(config, data)

    const getRatioChartProps = (
      config: ChartConfig,
      data: CompassAPIMultiSeriesChartData,
      title: string,
      seriesNames: string[]
    ) =>
      getMultipleLineChartProps(
        Context,
        {
          addMinMaxIndicators: true,
          chartTitle: title,
          msdIndicators: { avg: true, sd1: true, sd2: true },
          msdIndicatorsSeriesIndexes: [0],
          yAxis: [
            { height: '60%', id: 'primary', title: { text: '' } },
            { height: '40%', id: 'secondary', title: { text: '' }, top: '60%' },
          ],
        },
        {
          name: seriesNames,
          yAxis: ['primary', 'secondary', 'secondary'],
        }
      )(config, data)
  }

  export namespace RadarBrentSeries {
    const CONTEXT_ID = 'equity:oil:radar_brent_series'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      context: typeof CONTEXT_ID
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = data.data?.map((s, idx) => ({
        color: idx === 0 ? '#16164D' : '#297F0D',
        data: s.data.map(({ date, value }) => [
          new Date(date).getTime(),
          value,
        ]),
        id: `series-${idx}`,
        name: capitalizeString(s.name),
        type: 'line',
        tooltip: {
          valueDecimals: 2,
          valueSuffix: '%',
          xDateFormat: '%A, %b %e, %Y',
        },
        yAxis: idx === 0 ? 'relative' : 'absolute',
      }))

      const creditsConfig = getDataSources(data.data_sources)

      const yAxis = [
        { id: 'relative', opposite: false, suffix: '%' },
        { id: 'absolute', opposite: true },
      ]

      return {
        id: Context.getChartId(config),
        title: 'Radar vs. Brent',
        options: mergeDeepAll([
          creditsConfig,
          {
            tooltip: { shared: false, split: true },
            series,
            yAxis,
          },
        ]),
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => config.context,
      getChartData: getCompassAPIChartData,
      getChartProps,
    }
  }

  export namespace Radar {
    const CONTEXT_ID = 'equity:oil:radar'

    export const __typelist: {
      config: ChartConfig
      data: ChartData
      context: ContextType
    } = {} as any

    export type ContextType = BaseContext<
      typeof CONTEXT_ID,
      ChartConfig,
      ChartData
    >

    export interface ChartConfig extends CommonChartConfig {
      context: typeof CONTEXT_ID
      value: number
      date: number
    }

    export type ChartData = CompassAPIMultiSeriesChartData

    const getChartProps = (config: ChartConfig, data: ChartData) => {
      const series = [
        {
          color: '#16164D',
          data: data.data
            ? data.data
                .map(
                  (s: any) =>
                    s.data.filter(
                      (point: any) =>
                        new Date(point.date).getTime() === config.date
                    )[0]?.value ?? null
                )
                .slice(0, 5)
            : [],
          id: `radar-series`,
          fillOpacity: 0.5,
          name: 'Radar',
          showInLegend: false,
          type: 'area',
          yAxis: 'default',
        },
      ]

      const creditsConfig = getDataSources(data.data_sources)

      const xAxis = [
        {
          categories: [
            'Estrutura da curva',
            'Margem de refino',
            'Demanda',
            'Posição especulativa',
            'Preço vs. Estoque relativo',
          ],
          tickmarkPlacement: 'on',
        },
      ]

      const yAxis = [
        {
          id: 'default',
          gridLineInterpolation: 'circle',
          lineWidth: 0,
          min: 0,
        },
      ]

      return {
        id: Context.getChartId(config),
        title: config.title,
        options: mergeDeepAll([
          creditsConfig,
          {
            chart: { polar: true, type: 'line' },
            tooltip: { shared: false, split: true },
            series,
            yAxis,
            xAxis,
          },
        ]),
        useRegularChart: true,
      }
    }

    export const Context: ContextType = {
      id: CONTEXT_ID,
      getChartId: (config) => `${config.context}:${config.date}`,
      getChartData: getCompassAPIChartData,
      getChartProps,
    }
  }
}
